import { useState } from "react"
import { useParams } from "react-router-dom"
import { useQuery, useMutation } from "@tanstack/react-query"
import {
  getPublicQuoteAmendment,
  acceptPublicQuoteAmendment,
  rejectPublicQuoteAmendment,
} from "@/services/publicQuote"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { Textarea } from "@/components/ui/textarea"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/function"
import { serverDetail } from "@/services/public-fetch"
import { useToast } from "@/hooks/use-toast"
import { FileText, CheckCircle2, XCircle, AlertTriangle, Info } from "lucide-react"
import { formatQuantity } from "@/lib/money"
import { formatIsoDateFr } from "@/lib/organisation-day"

function formatMoney(value: string | number): string {
  const n = typeof value === "string" ? parseFloat(value) : value
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(
    Number.isFinite(n) ? n : 0,
  )
}

function formatSignedMoney(value: string | number): string {
  const n = typeof value === "string" ? parseFloat(value) : value
  const safe = Number.isFinite(n) ? n : 0
  const formatted = formatMoney(Math.abs(safe))
  return safe < 0 ? `- ${formatted}` : `+ ${formatted}`
}

function formatDate(iso: string): string {
  return formatIsoDateFr(iso, { day: "2-digit", month: "long", year: "numeric" })
}

const CHANGE_TYPE_LABELS: Record<"add" | "modify" | "remove", string> = {
  add: "Ajout",
  modify: "Modification",
  remove: "Retrait",
}

function changeTypeBadge(code: string) {
  const label = CHANGE_TYPE_LABELS[code as "add" | "modify" | "remove"] ?? code
  const variant: "default" | "secondary" | "destructive" =
    code === "add" ? "default" : code === "remove" ? "destructive" : "secondary"
  return (
    <Badge variant={variant} data-test="amendment-change-type-badge">
      {label}
    </Badge>
  )
}

/** Mention relue au particulier : l'avenant au contrat a son délai. */
export const AMENDMENT_WITHDRAWAL_NOTICE =
  "En acceptant cet avenant au devis, vous acceptez que nous préparions un avenant à votre contrat. Cet avenant en modifiera l'objet et le prix. Votre contrat ne sera modifié qu'à la signature de l'avenant au contrat. À cette signature, vous disposerez d'un délai de rétractation de dix jours (quatorze jours s'il est conclu à distance ou hors établissement). Aucun paiement ne pourra vous être demandé avant la fin de ce délai. Si vous vous rétractez, votre contrat d'origine continue de s'appliquer. Si vous n'acceptez pas ces modifications, n'acceptez pas cet avenant au devis : votre contrat d'origine continue de s'appliquer."

// Encadré du particulier, affiché avant « Accepter cet avenant ».
export function AmendmentWithdrawalNotice() {
  return (
    <div className="flex items-start gap-2 rounded-md border border-primary/30 bg-primary/5 px-4 py-3 text-sm text-foreground" data-test="amendment-b2c-notice">
      <Info className="mt-0.5 size-4 shrink-0 text-primary" />
      <p>{AMENDMENT_WITHDRAWAL_NOTICE}</p>
    </div>
  )
}

export default function PublicQuoteAmendmentPage() {
  const { token } = useParams<{ token: string }>()
  const { toast } = useToast()
  const [acceptOpen, setAcceptOpen] = useState(false)
  const [rejectOpen, setRejectOpen] = useState(false)

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["public-quote-amendment", token],
    queryFn: () => getPublicQuoteAmendment(token!),
    enabled: !!token,
    retry: false,
  })

  if (!token) {
    return (
      <PublicShell>
        <ErrorState message="Token manquant" />
      </PublicShell>
    )
  }

  if (isLoading) {
    return (
      <PublicShell>
        <div className="p-8 text-center text-muted-foreground">Chargement de l'avenant…</div>
      </PublicShell>
    )
  }

  if (isError || !data) {
    // 410 Gone (avenant résolu) et 404 tombent tous deux ici : le lien n'est
    // plus actionnable, on affiche un état friendly plutôt qu'une page blanche.
    return (
      <PublicShell>
        <ErrorState
          message={pipe(
            serverDetail(error),
            O.getOrElse(() => "Cet avenant n'est plus disponible (déjà traité, annulé ou expiré)."),
          )}
        />
      </PublicShell>
    )
  }

  const canAct = !data.is_expired && data.status_code === "sent"
  const isParticulier = data.is_individual

  return (
    <PublicShell tenantName={data.tenant_name} tenantLogo={data.tenant_logo_url}>
      <div className="mx-auto max-w-3xl space-y-6 p-4 md:p-8" data-test="public-quote-amendment-page">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold" data-test="amendment-title">
              Devis {data.quote_number}
            </h1>
            <p className="text-sm text-muted-foreground" data-test="amendment-number">
              Avenant n°{data.amendment_number}
            </p>
          </div>
          <Badge variant="default" data-test="amendment-status-badge">
            {data.is_expired
              ? "Expiré"
              : data.status_code === "sent"
                ? "En attente de signature"
                : "Traité"}
          </Badge>
        </div>

        {data.is_expired && (
          <div className="flex items-center gap-2 rounded-md border border-orange-200 bg-orange-50 px-4 py-3 text-orange-900">
            <AlertTriangle className="h-5 w-5" />
            <span>
              Cet avenant a expiré
              {data.validity_date ? ` le ${formatDate(data.validity_date)}` : ""}. Contactez votre
              interlocuteur pour en obtenir un nouveau.
            </span>
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Modifications proposées</CardTitle>
          </CardHeader>
          <CardContent>
            {data.notes && (
              <p className="mb-4 whitespace-pre-wrap text-sm text-muted-foreground" data-test="amendment-notes">
                {data.notes}
              </p>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-test="amendment-lines-table">
                <thead className="border-b text-left text-muted-foreground">
                  <tr>
                    <th className="py-2 pr-2">Nature</th>
                    <th className="py-2 pr-2">Prestation</th>
                    <th className="py-2 pr-2 text-right">Qté</th>
                    <th className="py-2 pr-2 text-right">Prix unitaire</th>
                    <th className="py-2 text-right">Impact HT</th>
                  </tr>
                </thead>
                <tbody>
                  {data.lines.map((line, idx) => (
                    <tr key={idx} className="border-b last:border-0" data-test="amendment-line-row">
                      <td className="py-3 pr-2">{changeTypeBadge(line.change_type)}</td>
                      <td className="py-3 pr-2 font-medium">{line.description}</td>
                      <td className="py-3 pr-2 text-right">
                        {formatQuantity(line.quantity)}
                        {line.unit ? ` ${line.unit}` : ""}
                      </td>
                      <td className="py-3 pr-2 text-right">{formatMoney(line.unit_price)}</td>
                      <td
                        className={`py-3 text-right font-medium ${
                          parseFloat(String(line.line_delta_ht)) < 0 ? "text-rose-600" : "text-emerald-600"
                        }`}
                        data-test="amendment-line-delta"
                      >
                        {formatSignedMoney(line.line_delta_ht)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex justify-end">
              <div className="w-full max-w-xs space-y-1 text-sm">
                <Row label="Total actuel HT" value={formatMoney(data.base_total_ht)} />
                <Row
                  label="Variation HT"
                  value={
                    <span data-test="amendment-delta-ht">{formatSignedMoney(data.total_amount_delta_ht)}</span>
                  }
                />
                <div className="border-t pt-2">
                  <Row
                    label={<span className="text-base font-semibold">Nouveau total HT</span>}
                    value={
                      <span className="text-base font-semibold" data-test="amendment-effective-ht">
                        {formatMoney(data.effective_total_ht)}
                      </span>
                    }
                  />
                </div>
                <Row
                  label="Variation TTC"
                  value={
                    <span data-test="amendment-delta-ttc">{formatSignedMoney(data.total_amount_delta_ttc)}</span>
                  }
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700" data-test="amendment-legal-mention">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
          <p>{data.legal_mention}</p>
        </div>

        {isParticulier && <AmendmentWithdrawalNotice />}

        {canAct && (
          <div className="flex flex-wrap items-center gap-3" data-test="public-amendment-actions">
            <Button onClick={() => setAcceptOpen(true)} data-test="amendment-accept-btn">
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Accepter cet avenant
            </Button>
            <Button variant="outline" onClick={() => setRejectOpen(true)} data-test="amendment-reject-btn">
              <XCircle className="mr-2 h-4 w-4" />
              Refuser
            </Button>
          </div>
        )}

        <footer className="border-t pt-4 text-xs text-muted-foreground">
          Organisme de formation référencé Qualiopi. Pour toute question, contactez votre interlocuteur dédié.
        </footer>

        <AcceptDialog
          open={acceptOpen}
          onOpenChange={setAcceptOpen}
          token={token}
          onAccepted={() => {
            setAcceptOpen(false)
            void refetch()
            toast({ title: "Avenant accepté", description: "Merci, votre acceptation a bien été enregistrée." })
          }}
        />
        <RejectDialog
          open={rejectOpen}
          onOpenChange={setRejectOpen}
          token={token}
          onRejected={() => {
            setRejectOpen(false)
            void refetch()
            toast({ title: "Avenant refusé", description: "Votre refus a bien été enregistré." })
          }}
        />
      </div>
    </PublicShell>
  )
}

function PublicShell({
  tenantName,
  tenantLogo,
  children,
}: {
  tenantName?: string
  tenantLogo?: string | null
  children: React.ReactNode
}) {
  return (
    <div className="min-h-screen bg-muted/30">
      <header className="border-b bg-white">
        <div className="mx-auto flex max-w-3xl items-center gap-3 p-4">
          {tenantLogo && <img src={tenantLogo} alt={tenantName || "Organisme"} className="h-10 w-auto" />}
          <div className="flex items-center gap-2 text-base font-medium">
            <FileText className="h-5 w-5 text-primary" />
            {tenantName || "Qualneo"}
          </div>
        </div>
      </header>
      <main>{children}</main>
    </div>
  )
}

function Row({ label, value }: { label: React.ReactNode; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <div className="text-muted-foreground">{label}</div>
      <div>{value}</div>
    </div>
  )
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="mx-auto mt-12 max-w-md p-8 text-center" data-test="amendment-error-state">
      <AlertTriangle className="mx-auto h-10 w-10 text-orange-500" />
      <h2 className="mt-3 text-lg font-semibold">Lien indisponible</h2>
      <p className="mt-2 text-sm text-muted-foreground">{message}</p>
    </div>
  )
}

function AcceptDialog({
  open,
  onOpenChange,
  token,
  onAccepted,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  token: string
  onAccepted: () => void
}) {
  const { toast } = useToast()
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [cgu, setCgu] = useState(false)

  const mutation = useMutation({
    mutationFn: () =>
      acceptPublicQuoteAmendment(token, { name: name.trim(), email: email.trim(), cgu_accepted: cgu }),
    onSuccess: onAccepted,
    onError: (e: Error) => toast({ variant: "destructive", title: "Erreur", description: e.message }),
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-test="amendment-accept-dialog">
        <DialogHeader>
          <DialogTitle>Confirmer l'acceptation de l'avenant</DialogTitle>
          <DialogDescription>
            Cette action est définitive. Merci de confirmer votre identité et votre accord pour les
            modifications proposées.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700" data-test="amendment-accept-legal-notice">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
            <p>
              En cliquant « Accepter cet avenant », vous signez électroniquement cette modification
              (art. 1366 Code civil). Votre identité, adresse IP et horodatage sont enregistrés comme
              preuve de votre consentement.
            </p>
          </div>
          <div>
            <Label htmlFor="amendment-accept-name">Nom complet</Label>
            <Input
              id="amendment-accept-name"
              data-test="amendment-accept-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Prénom Nom"
            />
          </div>
          <div>
            <Label htmlFor="amendment-accept-email">Email</Label>
            <Input
              id="amendment-accept-email"
              type="email"
              data-test="amendment-accept-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="vous@exemple.fr"
            />
          </div>
          <div className="flex items-start gap-2">
            <Checkbox
              id="amendment-accept-cgu"
              data-test="amendment-accept-cgu"
              checked={cgu}
              onCheckedChange={(v) => setCgu(v === true)}
            />
            <Label htmlFor="amendment-accept-cgu" className="text-sm font-normal">
              J'accepte les modifications et les conditions de cet avenant.
            </Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            data-test="amendment-accept-submit"
            onClick={() => mutation.mutate()}
            disabled={!cgu || !name.trim() || !email.trim() || mutation.isPending}
          >
            {mutation.isPending ? "Validation…" : "Confirmer l'acceptation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function RejectDialog({
  open,
  onOpenChange,
  onRejected,
  token,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  onRejected: () => void
  token: string
}) {
  const { toast } = useToast()
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [reason, setReason] = useState("")

  const mutation = useMutation({
    mutationFn: () =>
      rejectPublicQuoteAmendment(token, {
        name: name.trim(),
        email: email.trim(),
        reason: reason.trim() || undefined,
      }),
    onSuccess: onRejected,
    onError: (e: Error) => toast({ variant: "destructive", title: "Erreur", description: e.message }),
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-test="amendment-reject-dialog">
        <DialogHeader>
          <DialogTitle>Confirmer le refus de l'avenant</DialogTitle>
          <DialogDescription>
            Vous pouvez préciser la raison de votre refus (facultatif). Le devis initial reste en
            vigueur, inchangé.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="amendment-reject-name">Nom complet</Label>
            <Input
              id="amendment-reject-name"
              data-test="amendment-reject-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="amendment-reject-email">Email</Label>
            <Input
              id="amendment-reject-email"
              type="email"
              data-test="amendment-reject-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="amendment-reject-reason">Raison (facultatif)</Label>
            <Textarea
              id="amendment-reject-reason"
              data-test="amendment-reject-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            data-test="amendment-reject-submit"
            variant="destructive"
            onClick={() => mutation.mutate()}
            disabled={!name.trim() || !email.trim() || mutation.isPending}
          >
            {mutation.isPending ? "Validation…" : "Confirmer le refus"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
